import React, { useState, useEffect, useCallback } from 'react';
import { Col, Dropdown, DropdownMenu, DropdownToggle, Row, Spinner } from 'reactstrap';
import { Link, useNavigate } from 'react-router-dom';
import SimpleBar from "simplebar-react";
import { notificationsApi } from '../../services/api';
import { POLLING_INTERVALS, createSmartInterval } from '../../utils/polling';

// Notification type from API
interface Notification {
  id: number;
  announcement_id: number;
  title: string;
  message: string;
  type: string; // info, warning, success, error
  link_url?: string;
  link_text?: string;
  is_read: boolean;
  is_dismissed: boolean;
  created_at: string;
  priority: number;
}

// Helper to format time ago
const formatTimeAgo = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (seconds < 60) return 'Just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} hrs ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)} days ago`;
  return date.toLocaleDateString();
};

// Get icon based on notification type
const getTypeIcon = (type: string): { icon: string; bgClass: string; textClass: string } => {
  switch (type) {
    case 'success':
      return { icon: 'bx bx-check-circle', bgClass: 'bg-success-subtle', textClass: 'text-success' };
    case 'warning':
      return { icon: 'bx bx-error', bgClass: 'bg-warning-subtle', textClass: 'text-warning' };
    case 'error':
      return { icon: 'bx bx-x-circle', bgClass: 'bg-danger-subtle', textClass: 'text-danger' };
    case 'info':
    default:
      return { icon: 'bx bx-info-circle', bgClass: 'bg-info-subtle', textClass: 'text-info' };
  }
};

const NotificationDropdown = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await notificationsApi.getNotifications({ limit: 10 });
      if (response) {
        setNotifications(response.notifications || []);
        setUnreadCount(response.unread_count || 0);
      }
    } catch (err: any) {
      console.error('Failed to fetch notifications:', err);
      setError('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch unread count only (for polling)
  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await notificationsApi.getUnreadCount();
      if (response) {
        setUnreadCount(response.count || 0);
      }
    } catch (err) {
      // Silent fail for polling
    }
  }, []);

  // Initial fetch and polling
  useEffect(() => {
    fetchNotifications();

    // Poll for unread count every 60 seconds
    const cleanup = createSmartInterval(
      fetchUnreadCount,
      POLLING_INTERVALS.NOTIFICATIONS_UNREAD,
      { runImmediately: false, runOnVisible: true }
    );
    return () => cleanup();
  }, [fetchNotifications, fetchUnreadCount]);

  // Fetch full list when dropdown opens
  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen, fetchNotifications]);

  // Mark notification as read
  const handleNotificationClick = async (notification: Notification) => {
    if (!notification.is_read) {
      try {
        await notificationsApi.markAsRead(notification.id);
        setNotifications(prev =>
          prev.map(n => n.id === notification.id ? { ...n, is_read: true } : n)
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      } catch (err) {
        console.error('Failed to mark as read:', err);
      }
    }

    // Open link in new tab if link_url exists
    if (notification.link_url) {
      setIsOpen(false);
      // External links or internal links - open in new tab
      const url = notification.link_url.startsWith('http')
        ? notification.link_url
        : window.location.origin + notification.link_url;
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  // Dismiss notification
  const handleDismiss = async (e: React.MouseEvent, notificationId: number) => {
    e.stopPropagation();
    try {
      await notificationsApi.dismiss(notificationId);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      // Update unread count if dismissed notification was unread
      const dismissedNotif = notifications.find(n => n.id === notificationId);
      if (dismissedNotif && !dismissedNotif.is_read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (err) {
      console.error('Failed to dismiss notification:', err);
    }
  };

  // Mark all as read
  const handleMarkAllRead = async () => {
    try {
      await notificationsApi.markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    }
  };

  return (
    <React.Fragment>
      <Dropdown
        isOpen={isOpen}
        toggle={() => setIsOpen(!isOpen)}
        className="topbar-head-dropdown ms-1 header-item"
      >
        <DropdownToggle
          type="button"
          tag="button"
          className="btn btn-icon btn-topbar btn-ghost-secondary rounded-circle"
        >
          <i className='bx bx-bell fs-22'></i>
          {unreadCount > 0 && (
            <span className="position-absolute topbar-badge fs-10 translate-middle badge rounded-pill bg-danger">
              {unreadCount > 99 ? '99+' : unreadCount}
              <span className="visually-hidden">unread notifications</span>
            </span>
          )}
        </DropdownToggle>

        <DropdownMenu className="dropdown-menu-lg dropdown-menu-end p-0">
          {/* Header */}
          <div className="dropdown-head bg-primary bg-pattern rounded-top">
            <div className="p-3">
              <Row className="align-items-center">
                <Col>
                  <h6 className="m-0 fs-16 fw-semibold text-white">Notifications</h6>
                </Col>
                <div className="col-auto dropdown-tabs">
                  {unreadCount > 0 && (
                    <span className="badge bg-light-subtle fs-13 text-body">
                      {unreadCount} New
                    </span>
                  )}
                </div>
              </Row>
            </div>

            {/* Mark all read button */}
            {unreadCount > 0 && (
              <div className="px-3 pb-2">
                <button
                  type="button"
                  className="btn btn-sm btn-light w-100"
                  onClick={handleMarkAllRead}
                >
                  <i className="ri-check-double-line me-1"></i>
                  Mark all as read
                </button>
              </div>
            )}
          </div>

          {/* Content */}
          <div className="py-2 ps-2">
            <SimpleBar style={{ maxHeight: "300px" }} className="pe-2">
              {loading && notifications.length === 0 ? (
                <div className="text-center py-4">
                  <Spinner size="sm" color="primary" />
                  <p className="text-muted mt-2 mb-0">Loading notifications...</p>
                </div>
              ) : error ? (
                <div className="text-center py-4">
                  <i className="bx bx-error-circle text-danger fs-24"></i>
                  <p className="text-muted mt-2 mb-0">{error}</p>
                  <button
                    type="button"
                    className="btn btn-sm btn-soft-primary mt-2"
                    onClick={fetchNotifications}
                  >
                    Retry
                  </button>
                </div>
              ) : notifications.length === 0 ? (
                <div className="text-center py-4">
                  <i className="bx bx-bell-off text-muted fs-24"></i>
                  <p className="text-muted mt-2 mb-0">No notifications yet</p>
                </div>
              ) : (
                notifications.map((notification) => {
                  const typeInfo = getTypeIcon(notification.type);
                  return (
                    <div
                      key={notification.id}
                      className={`text-reset notification-item d-block dropdown-item position-relative ${!notification.is_read ? 'active' : ''}`}
                      onClick={() => handleNotificationClick(notification)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="d-flex">
                        <div className="avatar-xs me-3 flex-shrink-0">
                          <span className={`avatar-title ${typeInfo.bgClass} ${typeInfo.textClass} rounded-circle fs-16`}>
                            <i className={typeInfo.icon}></i>
                          </span>
                        </div>
                        <div className="flex-grow-1">
                          <h6 className="mt-0 mb-1 fs-13 fw-semibold">
                            {notification.title}
                            {!notification.is_read && (
                              <span className="badge bg-primary ms-2" style={{ fontSize: '9px' }}>NEW</span>
                            )}
                          </h6>
                          <p className="mb-1 fs-13 text-muted" style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical'
                          }}>
                            {notification.message}
                          </p>
                          <p className="mb-0 fs-11 fw-medium text-uppercase text-muted">
                            <span>
                              <i className="mdi mdi-clock-outline me-1"></i>
                              {formatTimeAgo(notification.created_at)}
                            </span>
                            {notification.link_url && notification.link_text && (
                              <span className="ms-2 text-primary">
                                {notification.link_text} <i className="ri-arrow-right-line"></i>
                              </span>
                            )}
                          </p>
                        </div>
                        <div className="px-2 fs-15">
                          <button
                            type="button"
                            className="btn btn-sm btn-ghost-secondary rounded-circle"
                            onClick={(e) => handleDismiss(e, notification.id)}
                            title="Dismiss"
                          >
                            <i className="ri-close-line"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}

              {/* View All button */}
              {notifications.length > 0 && (
                <div className="my-3 text-center">
                  <Link
                    to="/notifications"
                    className="btn btn-soft-success waves-effect waves-light"
                    onClick={() => setIsOpen(false)}
                  >
                    View All Notifications
                    <i className="ri-arrow-right-line align-middle ms-1"></i>
                  </Link>
                </div>
              )}
            </SimpleBar>
          </div>
        </DropdownMenu>
      </Dropdown>
    </React.Fragment>
  );
};

export default NotificationDropdown;
